import React, { memo, useEffect, useState } from "react";
import ReactHtmlParser from "react-html-parser";
import { NumericFormat } from "react-number-format";
import { useDispatch, useSelector } from "react-redux";
import { calc, generatePDF, twoDecimal } from "../../utils";
import { Box, Button, Card, CardActions, CardContent, Grid, Typography, Divider, Checkbox } from "@mui/material";
import { AddCircle, Info } from "@mui/icons-material";
import { addOutput, clearOutputs, selectOutputById, selectOutputTypeTotalByProp } from "./outputsSlice";
import { selectLabeledInputValues } from "./inputsSlice";
import { selectOutputs, selectOutputIdsByType, editOutput, editOutputs, updateCheckbox } from "./outputsSlice";
import { updateView } from "./settingsSlice";
import { OutputItem } from "./OutputItem";
import { LineItem } from "../common/LineItem";
import { Total } from "./Total";
import { Percent } from "./Percent";

const WrappedComponent = ({ type, headers, instructions, footnote }) => {
	const dispatch = useDispatch();
	const { rate, wage, amenities, cleaning, laundry, rooms, nights } = useSelector(selectLabeledInputValues);
	const view = useSelector(state => state.channelMix.settings.view);
	const outputs = useSelector(state => selectOutputIdsByType(state, type));
	const outputItems = useSelector(selectOutputs);
	const first = useSelector(state => selectOutputById(state, type === "cost" ? 0 : 13));
	const totals = !!first ? Object.keys(first).filter((name) => (name !== "id" && name !== "expense" && name !== "type")) : [];
	const fullDays = useSelector(state => state.channelMix.outputs.fullDays);
	const lightDays = useSelector(state => state.channelMix.outputs.lightDays);
	const totalRev = useSelector(state => selectOutputTypeTotalByProp(state, {type: "weekly", prop: "total"}));
	const [update, setUpdate] = useState(0);
	const checkboxes = [
		{label: "Full Service Days", type: "fullDays", checks: fullDays},
		{label: "Light Touch Days", type: "lightDays", checks: lightDays}
	];

	const weeklyProfit = ((rate*7)*rooms)-(totalRev*rooms);
	const totalProfit = ((rate*7)*nights)-(totalRev*nights);
	const weeklyRevCalc = (rate*7)*rooms;
	const totalRevCalc = (rate*7)*nights;

	const extras = [
		[weeklyRevCalc, totalRevCalc],
		[(totalRev*rooms), (totalRev*nights)],
		[weeklyProfit, totalProfit],
		[((weeklyProfit / weeklyRevCalc)*100), ((totalProfit / totalRevCalc)*100)]
	];

	const rate35 = rate*0.65;
	const rate50 = rate*0.5;

	const netRate = () => {
		const rateData = {expense: "NET RATE", noncp: rate, cp: rate, gdscp: rate, gdsnoncp: rate, booking: rate, expedia: rate, op35: rate35, op50: rate50};
		return Object.keys(rateData).map((name) => {
			return {label: name, value: rateData[name], adornment: name !== "expense" && "$", readOnly: true}
		});
	};

	const add = () => {
		dispatch(addOutput(type));
	};

	const wrLabor = (day) => fullDays[day] ? wage*(40/60) : 0.00;
	const ltLabor = (day) => lightDays[day] ? wage*0.167 : 0.00;

	const checkboxChange = (e, type, prop) => {
		const { checked } = e.target
		dispatch(updateCheckbox({type, prop, checked}))
		if(type === "fullDays") {
			dispatch(editOutput({id: 13, changes: {[prop]: checked ? wage*(40/60) : 0.00}}));
		}else {
			dispatch(editOutput({id: 14, changes: {[prop]: checked ? wage*0.167 : 0.00}}));
		}
	};

	const resetMix = () => {
		dispatch(updateView("input"));
		dispatch(clearOutputs());
	};

	const print = () => {
		setTimeout(() => {
			generatePDF();
		}, 800);
	};

	useEffect(() => {
		for(const prop in fullDays) {
			if(view === "output") {
				const cond = (!fullDays[prop] && !lightDays[prop]);
				const list = [
					{id: 13, changes: {total: calc(wrLabor)}},
					{id: 14, changes: {total: calc(ltLabor)}},
					{id: 17, changes: {[prop]: cond ? 0.00 : amenities}},
					{id: 18, changes: {[prop]: cond ? 0.00 : wage*0.167}},
					{id: 19, changes: {[prop]: cond ? 0.00 : cleaning}},
					{id: 20, changes: {[prop]: cond ? 0.00 : laundry}}
				];
				dispatch(editOutputs(list));
			}
		}
		setUpdate(update + 1);
	}, [fullDays, lightDays]);

	useEffect(() => {
		const list = [
			{id: 17, changes: {total: calc(outputItems[17])}},
			{id: 18, changes: {total: calc(outputItems[18])}},
			{id: 19, changes: {total: calc(outputItems[19])}},
			{id: 20, changes: {total: calc(outputItems[20])}}
		];
		dispatch(editOutputs(list));
	}, [update]);

	return (
		<Card sx={{height: "100%"}}>
			<CardContent sx={{display: "flex", flexDirection: "column"}}>
				<Box sx={{p: 2, display: "flex", alignItems: "center"}}>
					<Info/>
					<Box>
						<Typography sx={{px: 2}}><i>{ReactHtmlParser(instructions)}</i></Typography>
					</Box>
				</Box>
				<Divider sx={{my: 2}} />
				{type === "cost" && <Typography><b>Costs of renting one room</b></Typography>}
				<Grid container spacing={0}>
					<Grid item xs={type === "weekly" ? 10 : 12}>
						<Box sx={{width: "100%", py: 2}}>
							<Box sx={{mr: "50px"}}>
								<Grid container spacing={1}>
									<Grid container spacing={0} item sx={{"& .MuiDivider-root": {borderRight: "1px solid rgba(0, 0, 0, 0.12)"}}}>
										<Grid item xs={type === "cost" ? 3 : 2}>
											<Divider sx={{borderLeft: "1px solid rgba(0, 0, 0, 0.12)"}}>
												<Typography variant="body2" sx={{px: .5, py: 1, textAlign: "center"}}>
													<b>EXPENSE</b>
												</Typography>
											</Divider>
										</Grid>
										<Grid item xs>
											<Divider>
												<Typography variant="body2" sx={{px: .5, py: 1, textAlign: "center"}}>
													<b>AMOUNT</b>
												</Typography>
											</Divider>
										</Grid>
									</Grid>
									<Grid container spacing={1} item>
										{headers.map(({label, textAlign}, key) =>
											<Grid key={`${type}_output_header_${key}`} item xs={key > 0 ? true : type === "cost" ? 3 : 2}>
												<Typography variant="body2" sx={{
													pl: .5, pr: 1.75, py: 1,
													fontSize: ".8rem",
													textAlign: type === "cost" ? "end" : "center",
													height: "100%"
												}}>
													<b>{label}</b>
												</Typography>
											</Grid>
										)}
									</Grid>
								</Grid>
							</Box>
							<Grid container spacing={1} sx={{pr: "42px"}}>
								<Grid container item spacing={1} xs>
									{type === "cost" ? netRate().map(({ label, value, adornment, readOnly }, key) =>
										<Grid key={`expense_${key}`} item xs={label === "expense" ? type === "cost" ? 3 : 2 : true}>
											<LineItem {...{label, value, adornment, readOnly}} />
										</Grid>
									) : checkboxes.map(({ label, type, checks }, key) =>
										<Grid key={`checkboxes_${key}`} container item spacing={1} xs={12}>
											<Grid item xs={2}>
												<Typography sx={{py: 1, pl: .5, pr: "14px", fontSize: ".8rem", textAlign: "end", fontWeight: "bold"}}>{label}</Typography>
											</Grid>
											{Object.keys(checks).map((key) =>
												<Grid key={`${type}_check_${key}`} item xs sx={{display: "flex", justifyContent: "center"}}>
													<Checkbox
														checked={checks[key]}
														onChange={(e) => checkboxChange(e, type, key)}
														inputProps={{"aria-label": `${label} Checkbox (${key})`}}
													/>
												</Grid>
											)}
											<Grid item xs>
												<Typography sx={{textAlign: "center"}}>
													{Object.keys(checks).reduce((a, v) => a + (checks[v] === true ? 1 : 0), 0)}
												</Typography>
											</Grid>
										</Grid>
									)}
								</Grid>
							</Grid>
							<Divider sx={{my: 2}} />
							<Grid container spacing={1}>
								{outputs.map((id, key) =>
									<OutputItem key={`${type}_output_${key}`} {...{id}} />
								)}
							</Grid>
							<Box sx={{pt: 3, width: "100%", display: "flex", justifyContent: "center"}}>
								<Button
									variant="contained"
									color="success"
									onClick={add}
									sx={{
										p: .7,
										display: "flex", alignItems: "center", justifyContent: "center",
										boxShadow: "rgba(46, 125, 50, .2) 0px 0px 10px",
										height: "100%",
										width: "100%",
										borderRadius: 2
									}}
								>
									<AddCircle sx={{mr: 1}} />
									<Typography variant="button">Add New Line Item</Typography>
								</Button>
							</Box>
						</Box>
					</Grid>
				</Grid>
			</CardContent>
			<CardActions sx={{
				display: "flex",
				flexDirection: "column",
				p: 2,
				pt: type === "weekly" ? 3 : 2,
				"& .MuiBox-root": {
					width: "100%", borderBottom: 1, py: 2, borderColor: "grey.300", pr: 6.25
				}
			}}>
				<Grid container spacing={0} sx={{pb: 3}}>
					<Grid item xs>
						<Box sx={{mr: "50px", pt: "0 !important"}}>
							<Grid container spacing={1}>
								{headers.map(({label, textAlign}, key) =>
									<Grid key={`${type}_output_header_lower_${key}`} item xs={key > 0 ? true : type === "cost" ? 3 : 2}>
										<Typography variant="body2" sx={{
											pl: .5, pr: 1.75, py: 1,
											fontSize: ".8rem",
											textAlign: type === "cost" ? "end" : "center",
											height: "100%"
										}}>
											<b>{label}</b>
										</Typography>
									</Grid>
								)}
							</Grid>
						</Box>
						{type === "weekly" &&
							<Box>
								<Grid container item spacing={1}>
									<Grid item xs={2}>
										<Typography sx={{pl: 1.75}}><b>Revenue:</b></Typography>
									</Grid>
									{totals.map((item, key) =>
										<Grid key={`${type}_revenue_${key}`} item xs>
											<Typography sx={{textAlign: "end", pr: 1.75, fontWeight: "bold"}}>
												<NumericFormat
													value={twoDecimal(key > 6 ? rate*7 : rate)}
													prefix="$ "
													displayType="text"
													fixedDecimalScale
													allowLeadingZeros
													thousandSeparator=","
													decimalScale={2}
												/>
											</Typography>
										</Grid>
									)}
								</Grid>
							</Box>
						}
						<Box>
							<Grid container item spacing={1}>
								<Grid item xs={type === "cost" ? 3 : 2}>
									<Typography sx={{pl: 1.75}}><b>Total Marginal Costs:</b></Typography>
								</Grid>
								{totals.map((prop, key) =>
									<Grid key={`${type}_total_${key}`} item xs>
										<Total {...{type, prop}} />
									</Grid>
								)}
							</Grid>
						</Box>
						<Box>
							<Grid container item spacing={1}>
								<Grid item xs={type === "cost" ? 3 : 2}>
									<Typography sx={{pl: 1.75}}><b>{type === "cost" ? "Potential " : ""}Profit after Marginal Costs:</b></Typography>
								</Grid>
								{totals.map((prop, key) =>
									<Grid key={`${type}_total_profit_${key}`} container item xs alignItems="center" justifyContent="flex-end">
										<Total {...{type, prop}} margin={prop === "total" ? rate*7 : prop === "op35" ? rate35 : prop === "op50" ? rate50 : rate} />
									</Grid>
								)}
							</Grid>
						</Box>
						<Box>
							<Grid container item spacing={1}>
								<Grid item xs={type === "cost" ? 3 : 2}>
									<Typography sx={{pl: 1.75}}><b>% Potential Profit after Marginal Costs:</b></Typography>
								</Grid>
								{totals.map((prop, key) =>
									<Grid key={`${type}_percent_after_profit_${key}`} container item xs alignItems="center" justifyContent="flex-end">
										<Percent {...{type, prop}} margin={prop === "total" ? rate*7 : prop === "op35" ? rate35 : prop === "op50" ? rate50 : rate} />
									</Grid>
								)}
							</Grid>
						</Box>
					</Grid>
					{type === "weekly" &&
						<Grid item xs={2}>
							<Box sx={{mt: -1, pb: "6px !important", pt: "12px !important"}}>
								<Grid container item spacing={1} sx={{"& .MuiTypography-root": {fontSize: ".8rem", textAlign: "center"}}}>
									<Grid item xs>
										<Typography>Weekly potential for {rooms} rooms</Typography>
									</Grid>
									<Grid item xs>
										<Typography>Total potential for {rooms} rooms</Typography>
									</Grid>
								</Grid>
							</Box>
							{extras.map((group, groupKey) =>
								<Box key={`extra_${groupKey}`} sx={{py: (groupKey >= 2) ? "28px !important" : 2}}>
									<Grid container item spacing={1}>
										{group.map((value, key) =>
											<Grid key={`extra_item_${groupKey}_${key}`} item xs>
												{groupKey < 3
													? <Total {...{value}} />
													: <Percent value={value || "0"} />
												}
											</Grid>
										)}
									</Grid>
								</Box>
							)}
						</Grid>
					}
				</Grid>
				<Box sx={{width: "100%", display: "flex", justifyContent: "center", border: "unset !important", pr: "8px !important"}}>
					<Button
						variant="contained"
						onClick={resetMix}
						sx={{
							p: 3,
							cursor: "pointer",
							display: "flex", alignItems: "center", justifyContent: "center",
							boxShadow: "rgba(46, 125, 50, .2) 0px 0px 10px",
							width: "100%",
							borderRadius: 2
						}}
					>
						Restart
					</Button>
				</Box>
				<Box sx={{width: "100%", display: "flex", justifyContent: "center", border: "unset !important", pr: "8px !important"}}>
					<Button
						variant="contained"
						onClick={print}
						sx={{
							p: 3,
							cursor: "pointer",
							display: "flex", alignItems: "center", justifyContent: "center",
							boxShadow: "rgba(46, 125, 50, .2) 0px 0px 10px",
							width: "100%",
							borderRadius: 2
						}}
					>
						Print
					</Button>
				</Box>
				<Box sx={{p: 2, display: "flex", alignItems: "center", border: "unset !important"}}>
					<Typography sx={{px: 2, "& p": {pl: 1.5, textIndent: -12}}}><i>{ReactHtmlParser(footnote)}</i></Typography>
				</Box>
			</CardActions>
		</Card>
	);
};

export const Output = memo(WrappedComponent);